import React, { useEffect, useRef, useState } from 'react';
import { useTemplateStore } from './templateStore';
import { TemplatePlaceholderPalette } from './TemplatePlaceholderPalette';
import { TemplateStylePanel } from './TemplateStylePanel';
import { TemplateValidationPanel } from './TemplateValidationPanel';
import EdgedocsComposer from '../../../dms/components/composers/EdgedocsComposer';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { $createCACaptionNode } from '../../../dms/nodes/CACaptionNode';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';

export const TemplateBuilder: React.FC = () => {
  const { templates, selectedId, updateTemplate } = useTemplateStore();
  const [previewOpen, setPreviewOpen] = useState(false);
  const editorRef = useRef<any>(null);
  const t = templates.find(x => x.id === selectedId);
  if (!t) return <div className="p-6 text-sm text-gray-600">Select a template to edit.</div>;

  function insertPlaceholder(key: string) {
    const ed = editorRef.current; if (!ed) return;
    ed.update(() => {
      // Append token at end when no selection handling for MVP
      const p = $createParagraphNode();
      p.append($createTextNode(`{{${key}}}`));
      $getRoot().append(p);
    });
  }

  // Apply font from style tokens to live canvas
  useEffect(() => {
    const ed = editorRef.current; if (!ed) return;
    const rootEl = ed.getRootElement();
    if (!rootEl) return;
    const ff = (t as any).styleTokens?.fontFamily;
    const fs = (t as any).styleTokens?.fontSize;
    if (ff) rootEl.style.fontFamily = ff;
    if (fs) rootEl.style.fontSize = `${fs}pt`;
  }, [editorRef.current, t]);

  return (
    <div className="grid grid-cols-12 gap-4 p-6 text-gray-900 dark:text-gray-200">
      <div className="col-span-3 border rounded p-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 space-y-3">
        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Blocks</div>
        <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
          const ed = editorRef.current; if (!ed) return;
          ed.update(() => {
            const root = $getRoot();
            const node = $createCACaptionNode({
              attorneyBlock: 'John Q. Lawyer (SBN 123456)\nLaw Firm LLP\n123 Main St, Suite 500\nLos Angeles, CA 90012',
              courtName: 'Superior Court of California',
              countyName: 'County of Los Angeles',
              plaintiff: 'PLAINTIFF NAME',
              defendant: 'DEFENDANT NAME',
              caseNumber: '22STCV00001',
              dept: '44',
              judge: 'Hon. Jane Doe',
              title: 'NOTICE OF MOTION AND MOTION TO COMPEL',
              hearingDate: 'October 1, 2025',
              hearingTime: '8:30 a.m.',
              hearingLocation: 'Dept. 44, Stanley Mosk Courthouse',
              additionalParties: [],
              alignFields: true,
              titleBold: !!(t as any).captionStyle?.titleBold,
              titleItalic: !!(t as any).captionStyle?.titleItalic,
            });
            const first = root.getFirstChild();
            if (first) { first.insertBefore(node); } else { root.append(node); }
            // Remove baseline placeholder paragraphs at the top
            const children = root.getChildren();
            let removed = 0;
            for (let i = 0; i < children.length && removed < 10; i++) {
              const n: any = children[i];
              if (n.getType && n.getType() === 'ca-caption') continue;
              if (typeof n.getTextContent === 'function') {
                const txt = (n.getTextContent() || '').trim();
                const hasTokens = txt.includes('{{') && txt.includes('}}');
                const looksLikeCaseLine = /Case No\.:/.test(txt) && txt.includes('{{');
                if (!txt) { n.remove(); removed++; continue; }
                if (hasTokens || looksLikeCaseLine) { n.remove(); removed++; } else {
                  if (removed > 0) break;
                }
              }
            }
          });
        }}>Insert CA Caption</button>
        <div className="grid grid-cols-1 gap-2">
          <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
            const ed = editorRef.current; if (!ed) return;
            ed.update(() => { const p = $createParagraphNode(); p.append($createTextNode('{{INTRO}}')); $getRoot().append(p); });
          }}>Insert Intro</button>
          <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
            const ed = editorRef.current; if (!ed) return;
            ed.update(() => { const p = $createParagraphNode(); p.append($createTextNode('{{FACTS}}')); $getRoot().append(p); });
          }}>Insert Facts</button>
          <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
            const ed = editorRef.current; if (!ed) return;
            ed.update(() => { const p = $createParagraphNode(); p.append($createTextNode('{{ARGUMENT}}')); $getRoot().append(p); });
          }}>Insert Argument</button>
          <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
            const ed = editorRef.current; if (!ed) return;
            ed.update(() => { const p = $createParagraphNode(); p.append($createTextNode('{{PRAYER}}')); $getRoot().append(p); });
          }}>Insert Prayer</button>
          <button className="w-full px-2 py-1 border rounded text-sm" onClick={() => {
            const ed = editorRef.current; if (!ed) return;
            ed.update(() => { const p = $createParagraphNode(); p.append($createTextNode('{{SIGNATURE}}')); $getRoot().append(p); });
          }}>Insert Signature</button>
        </div>
        <TemplatePlaceholderPalette onInsert={insertPlaceholder} />
      </div>
      <div className="col-span-6 border rounded bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Template Canvas</h4>
            <input
              className="ml-4 px-2 py-1 border rounded text-sm"
              style={{ minWidth: 220 }}
              placeholder="Template name"
              value={(t as any).name || ''}
              onChange={(e) => updateTemplate(t.id, { name: e.target.value } as any)}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 text-sm border rounded"
              onClick={() => {
                try {
                  const ed = editorRef.current; if (!ed) return;
                  const json = ed.getEditorState().toJSON();
                  updateTemplate(t.id, { lexicalJson: json });
                } catch (e) {
                  console.error('Failed to save template content', e);
                }
              }}
            >
              Save Template
            </button>
            <button
              className="px-3 py-1 text-sm border rounded"
              onClick={() => {
                try {
                  const ed = editorRef.current; if (ed) {
                    const json = ed.getEditorState().toJSON();
                    updateTemplate(t.id, { lexicalJson: json });
                  }
                } finally {
                  // Simple close: simulate modal close by window.history back
                  if (window.history.length > 1) window.history.back();
                }
              }}
            >
              Save & Close
            </button>
            <button
              className="px-3 py-1 text-sm border rounded"
              onClick={() => {
                try {
                  const payload = { name: t.name, lexicalJson: t.lexicalJson, styleTokens: (t as any).styleTokens };
                  sessionStorage.setItem('webelieve.templatePreview', JSON.stringify(payload));
                  window.open('/dms/template-preview', '_blank');
                } catch (e) {
                  console.error('Failed to open template preview', e);
                  setPreviewOpen(true);
                }
              }}
            >
              Preview in EdgeDocs
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-[520px]">
          <EdgedocsComposer
            documentId={`template-${t.id}`}
            versionId=""
            initialContent={JSON.stringify(t.lexicalJson || {})}
            onClose={() => {}}
            readOnly={false}
            useStore={false}
            fullScreen={false}
            documentType="pleading"
            enablePlugins={true}
            pleadingPaperOptions={{ marginTopIn: (t as any).styleTokens?.margins?.top ?? 1, marginRightIn: (t as any).styleTokens?.margins?.right ?? 1, marginBottomIn: (t as any).styleTokens?.margins?.bottom ?? 1, marginLeftIn: (t as any).styleTokens?.margins?.left ?? 1, gutterIn: (t as any).styleTokens?.pleadingPaper?.gutter ?? 0.5, lineCount: (t as any).styleTokens?.pleadingPaper?.lineNumberEnd ?? 28 }}
            onEditorReady={() => { /* editorRef.current = ed; */ }}
          />
        </div>
      </div>
      <div className="col-span-3 border rounded p-3 space-y-4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <TemplateStylePanel templateId={t.id} />
        <TemplateValidationPanel templateId={t.id} />
      </div>
      <TemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} template={t as any} />
    </div>
  );
};

export default TemplateBuilder;


